import React, { useState, useRef, useCallback } from "react";
import {
  IconButton,
  Callout,
  Stack,
  Dropdown,
  TextField,
  PrimaryButton,
  DefaultButton,
  DirectionalHint,
  Text,
} from "@fluentui/react";

const filterOperators = [
  { key: "equals", text: "Is equal to" },
  { key: "notEquals", text: "Is not equal to" },
  { key: "startsWith", text: "Starts with" },
  { key: "contains", text: "Contains" },
  { key: "notContains", text: "Does not contain" },
  { key: "endsWith", text: "Ends with" },
];

const logicOperators = [
  { key: "and", text: "And" },
  { key: "or", text: "Or" },
];

const calloutStyles = {
  root: { padding: 16, minWidth: 250 },
};

const dropdownStyles = {
  dropdown: { width: "100%" },
};

const textFieldStyles = {
  root: { width: "100%" },
};

const buttonContainerStyles = {
  root: { marginTop: 12 },
};

/**
 * Applies a single filter condition to a value
 * @param {any} value - The value to test
 * @param {string} operator - The filter operator
 * @param {string} filterValue - The filter value to compare against
 * @returns {boolean} - Whether the value passes the filter
 */
const applyFilterCondition = (value, operator, filterValue) => {
  if (!filterValue && filterValue !== 0) return true;

  // Handle null/undefined values
  if (value === null || value === undefined) {
    const strValue = "";
    const strFilterValue = filterValue.toString().toLowerCase();

    switch (operator) {
      case "equals":
        return strValue === strFilterValue;
      case "notEquals":
        return strValue !== strFilterValue;
      case "startsWith":
        return strValue.startsWith(strFilterValue);
      case "contains":
        return strValue.includes(strFilterValue);
      case "notContains":
        return !strValue.includes(strFilterValue);
      case "endsWith":
        return strValue.endsWith(strFilterValue);
      default:
        return true;
    }
  }

  // Convert to string for comparison (handles numbers, dates, etc.)
  const strValue = value.toString().toLowerCase();
  const strFilterValue = filterValue.toString().toLowerCase();

  switch (operator) {
    case "equals":
      return strValue === strFilterValue;
    case "notEquals":
      return strValue !== strFilterValue;
    case "startsWith":
      return strValue.startsWith(strFilterValue);
    case "contains":
      return strValue.includes(strFilterValue);
    case "notContains":
      return !strValue.includes(strFilterValue);
    case "endsWith":
      return strValue.endsWith(strFilterValue);
    default:
      return true;
  }
};

/**
 * ColumnFilter component - Provides a filter dropdown for table columns
 * Supports multiple data types and filter operations
 */
const ColumnFilter = ({
  column,
  onFilterChange,
  currentFilter,
  iconStyles,
}) => {
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const [firstOperator, setFirstOperator] = useState(
    currentFilter?.firstOperator || "equals"
  );
  const [firstValue, setFirstValue] = useState(currentFilter?.firstValue || "");
  const [logicOperator, setLogicOperator] = useState(
    currentFilter?.logicOperator || "and"
  );
  const [secondOperator, setSecondOperator] = useState(
    currentFilter?.secondOperator || "equals"
  );
  const [secondValue, setSecondValue] = useState(
    currentFilter?.secondValue || ""
  );

  const iconButtonRef = useRef(null);

  const toggleCallout = useCallback((event) => {
    // Stop propagation to prevent column header click (sort) from triggering
    event.stopPropagation();
    setIsCalloutVisible((prev) => !prev);
  }, []);

  const dismissCallout = useCallback(() => {
    setIsCalloutVisible(false);
  }, []);

  const handleFilter = useCallback(() => {
    const filterConfig = {
      columnKey: column.fieldName || column.key,
      firstOperator,
      firstValue,
      logicOperator,
      secondOperator,
      secondValue,
      isActive: !!(firstValue || secondValue),
    };

    onFilterChange(filterConfig);
    dismissCallout();
  }, [
    column,
    firstOperator,
    firstValue,
    logicOperator,
    secondOperator,
    secondValue,
    onFilterChange,
    dismissCallout,
  ]);

  const handleClear = useCallback(() => {
    setFirstOperator("equals");
    setFirstValue("");
    setLogicOperator("and");
    setSecondOperator("equals");
    setSecondValue("");

    onFilterChange({
      columnKey: column.fieldName || column.key,
      isActive: false,
    });
    dismissCallout();
  }, [column, onFilterChange, dismissCallout]);

  const isFilterActive = currentFilter?.isActive;

  return (
    <>
      <IconButton
        elementRef={iconButtonRef}
        iconProps={{
          iconName: isFilterActive ? "FilterSolid" : "Filter",
        }}
        title={`Filter by ${column.name}`}
        ariaLabel={`Filter by ${column.name}`}
        onClick={toggleCallout}
        styles={{
          root: {
            height: 20,
            width: 20,
            marginLeft: 4,
            color: isFilterActive ? "#0078d4" : undefined,
          },
          icon: {
            fontSize: 12,
          },
          ...iconStyles,
        }}
      />
      {isCalloutVisible && (
        <Callout
          target={iconButtonRef.current}
          onDismiss={dismissCallout}
          directionalHint={DirectionalHint.bottomLeftEdge}
          setInitialFocus
          styles={calloutStyles}
        >
          <Stack tokens={{ childrenGap: 10 }}>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
              Show items with value that:
            </Text>

            {/* First filter condition */}
            <Dropdown
              selectedKey={firstOperator}
              options={filterOperators}
              onChange={(_, option) => setFirstOperator(option.key)}
              styles={dropdownStyles}
              ariaLabel="First filter operator"
            />
            <TextField
              value={firstValue}
              onChange={(_, value) => setFirstValue(value || "")}
              styles={textFieldStyles}
              placeholder="Enter value..."
              ariaLabel="First filter value"
            />

            {/* Logic operator */}
            <Dropdown
              selectedKey={logicOperator}
              options={logicOperators}
              onChange={(_, option) => setLogicOperator(option.key)}
              styles={{ dropdown: { width: 80 } }}
              ariaLabel="Logic operator"
            />

            {/* Second filter condition */}
            <Dropdown
              selectedKey={secondOperator}
              options={filterOperators}
              onChange={(_, option) => setSecondOperator(option.key)}
              styles={dropdownStyles}
              ariaLabel="Second filter operator"
            />
            <TextField
              value={secondValue}
              onChange={(_, value) => setSecondValue(value || "")}
              styles={textFieldStyles}
              placeholder="Enter value..."
              ariaLabel="Second filter value"
            />

            {/* Action buttons */}
            <Stack
              horizontal
              tokens={{ childrenGap: 8 }}
              horizontalAlign="end"
              styles={buttonContainerStyles}
            >
              <PrimaryButton text="FILTER" onClick={handleFilter} />
              <DefaultButton text="CLEAR" onClick={handleClear} />
            </Stack>
          </Stack>
        </Callout>
      )}
    </>
  );
};

/**
 * Applies column filters to items array
 * @param {Array} items - The items to filter
 * @param {Object} columnFilters - Object containing filter configs keyed by column
 * @returns {Array} - Filtered items
 */
export const applyColumnFilters = (items, columnFilters) => {
  if (!columnFilters || Object.keys(columnFilters).length === 0) {
    return items;
  }

  return items.filter((item) => {
    // Check each active column filter
    for (const columnKey in columnFilters) {
      const filter = columnFilters[columnKey];
      if (!filter || !filter.isActive) continue;

      const itemValue = item[columnKey];

      const firstConditionPasses = applyFilterCondition(
        itemValue,
        filter.firstOperator,
        filter.firstValue
      );

      // If there's no second value, just check the first condition
      if (!filter.secondValue && filter.secondValue !== 0) {
        if (!firstConditionPasses) return false;
        continue;
      }

      const secondConditionPasses = applyFilterCondition(
        itemValue,
        filter.secondOperator,
        filter.secondValue
      );

      // Apply logic operator
      let combinedResult;
      if (filter.logicOperator === "and") {
        combinedResult = firstConditionPasses && secondConditionPasses;
      } else {
        combinedResult = firstConditionPasses || secondConditionPasses;
      }

      if (!combinedResult) return false;
    }

    return true;
  });
};

export default ColumnFilter;
